import { ChevronDown, GitBranch, Plus } from "lucide-react";
import { useCanEdit } from "@/model/access";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { drivers } from "@/model/graph";
import { useStore } from "@/model/store";
import { isCalculated, scenariosTouching } from "./scenarioText";

/** Chips shown before the rest go into "+N more" (wide screens; phones scroll one row). */
const VISIBLE = 5;

/**
 * Baseline or a scenario, for the whole app: what charts show and what edits change.
 * With `nodeIds` (a node and everything upstream of it), only scenarios that set something
 * there are shown; the others can be added from the "+ Scenario" menu.
 */
export function ScenarioBar({ nodeIds }: { nodeIds?: string[] }) {
	const scenarios = useStore((s) => s.world.scenarios);
	const nodes = useStore((s) => s.world.nodes);
	const links = useStore((s) => s.world.links);
	const active = useStore((s) => s.activeScenario);
	const setActive = useStore((s) => s.setActiveScenario);
	const addScenario = useStore((s) => s.addScenario);
	const canEdit = useCanEdit();
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const [name, setName] = useState("");
	const listRef = useRef<HTMLDivElement>(null);
	const relevant = scenariosTouching(scenarios, nodeIds);
	// The active scenario stays visible even before it sets anything here, so it can be filled in.
	const shown = scenarios.filter((s) => relevant.includes(s) || s.id === active);
	const others = scenarios.filter((s) => !shown.includes(s));
	// Keep the active chip among the first few so it's never hidden behind "+N more".
	const activeAt = shown.findIndex((s) => s.id === active);
	const ordered = activeAt >= VISIBLE ? [...shown.slice(0, VISIBLE - 1), shown[activeAt], ...shown.slice(VISIBLE - 1).filter((s) => s.id !== active)] : shown;
	const overflow = ordered.slice(VISIBLE);

	// On phones the chips scroll in one row: bring the selected one into view.
	useEffect(() => {
		const list = listRef.current;
		const chip = list?.querySelector<HTMLElement>('[aria-selected="true"]');
		if (!list || !chip || list.scrollWidth <= list.clientWidth) return;
		const l = list.getBoundingClientRect();
		const c = chip.getBoundingClientRect();
		if (c.left < l.left || c.right > l.right) list.scrollLeft += c.left - l.left - 8;
	}, [active]);

	// On a calculated node the new scenario can't set this node, only its inputs (review 7.3).
	const here = nodeIds?.[0] ? nodes[nodeIds[0]] : undefined;
	const inputs = here && isCalculated(here) ? drivers(here.id, nodes, links).map((id) => nodes[id]) : [];

	async function create(openNode?: string) {
		const n = name.trim() || `Scenario ${scenarios.length + 1}`;
		if (!(await addScenario({ name: n }))) return;
		setName("");
		setOpen(false);
		if (openNode) navigate(`/node/${openNode}`);
	}

	/** Roving focus between chips with the arrow keys (review J2). */
	function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
		if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
		const tabs = [...(listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? [])].filter((t) => t.offsetParent !== null);
		if (!tabs.length) return;
		const i = tabs.indexOf(document.activeElement as HTMLButtonElement);
		const j = e.key === "Home" ? 0 : e.key === "End" ? tabs.length - 1 : e.key === "ArrowRight" ? (i + 1) % tabs.length : (i - 1 + tabs.length) % tabs.length;
		e.preventDefault();
		tabs[j].focus();
		tabs[j].scrollIntoView({ block: "nearest", inline: "nearest" });
	}

	return (
		<div className="flex min-w-0 items-center gap-1.5 md:flex-wrap">
			<span className="mr-1 shrink-0 text-xs text-muted-foreground">Scenario</span>
			<div
				ref={listRef}
				role="tablist"
				aria-label="Scenarios"
				onKeyDown={onKeyDown}
				className="-my-1 flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto px-0.5 py-1 [scrollbar-width:none] md:flex-initial md:flex-wrap md:overflow-visible"
			>
				<Chip active={!active} onClick={() => setActive(null)}>
					Baseline
				</Chip>
				{ordered.map((s, i) => (
					<Chip key={s.id} active={active === s.id} onClick={() => setActive(s.id)} scenario className={i >= VISIBLE ? "md:hidden" : undefined}>
						{s.name}
					</Chip>
				))}
			</div>
			{overflow.length > 0 && (
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button size="sm" variant="ghost" className="hidden shrink-0 md:inline-flex">
							+{overflow.length} more <ChevronDown className="size-3.5" />
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="start">
						{overflow.map((s) => (
							<DropdownMenuItem key={s.id} onSelect={() => setActive(s.id)}>
								<GitBranch className="size-3.5 text-[var(--scenario)]" />
								{s.name}
							</DropdownMenuItem>
						))}
					</DropdownMenuContent>
				</DropdownMenu>
			)}
			{canEdit && (
				<Popover open={open} onOpenChange={setOpen}>
					<PopoverTrigger asChild>
						<Button size="sm" variant="ghost" className="min-h-[32px] shrink-0 pointer-coarse:min-h-[44px]">
							<Plus className="size-4" /> Scenario
						</Button>
					</PopoverTrigger>
					<PopoverContent align="start" className="w-80 p-0">
						<form
							className="space-y-2 border-b p-3"
							onSubmit={(e) => {
								e.preventDefault();
								void create();
							}}
						>
							<div className="text-xs font-medium text-muted-foreground">New scenario</div>
							<div className="flex gap-2">
								<Input
									autoFocus
									value={name}
									onChange={(e) => setName(e.target.value)}
									placeholder="e.g. Acme exits in 2027"
									className="h-8"
									aria-label="Scenario name"
								/>
								<Button size="sm" type="submit">
									Create
								</Button>
							</div>
							{nodeIds &&
								(inputs.length ? (
									<p className="text-xs text-muted-foreground">
										Starts empty. Set values on one of its inputs:{" "}
										{inputs.map((n, i) => (
											<span key={n.id}>
												{i > 0 && ", "}
												<button
													type="button"
													className="font-medium text-foreground underline underline-offset-2 hover:text-[var(--scenario)]"
													onClick={() => void create(n.id)}
												>
													{n.title}
												</button>
											</span>
										))}
										.
									</p>
								) : (
									<p className="text-xs text-muted-foreground">Starts empty and opens on this page, ready to set this node’s values.</p>
								))}
						</form>
						{others.length > 0 && (
							<div className="max-h-64 overflow-y-auto p-1.5">
								<div className="px-2 pt-1 pb-1 text-xs font-medium text-muted-foreground">{nodeIds ? "Add an existing scenario" : "Other scenarios"}</div>
								{others.map((sc) => (
									<button
										key={sc.id}
										className="flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent"
										onClick={() => {
											setActive(sc.id);
											setOpen(false);
										}}
									>
										<GitBranch className="mt-0.5 size-3.5 shrink-0 text-[var(--scenario)]" />
										<span className="min-w-0">
											<span className="block truncate">{sc.name}</span>
											<span className="block truncate text-xs text-muted-foreground">
												{sc.levers.length ? `Sets ${sc.levers.length} other ${sc.levers.length === 1 ? "node" : "nodes"}` : "Nothing set yet"}
											</span>
										</span>
									</button>
								))}
							</div>
						)}
					</PopoverContent>
				</Popover>
			)}
			{nodeIds && others.length > 0 && (
				<span className="hidden text-xs text-muted-foreground md:inline">
					{others.length} other{others.length === 1 ? "" : "s"} don’t change this
				</span>
			)}
		</div>
	);
}

function Chip({
	active,
	onClick,
	children,
	scenario,
	className,
}: {
	active: boolean;
	onClick: () => void;
	children: React.ReactNode;
	scenario?: boolean;
	className?: string;
}) {
	return (
		<button
			role="tab"
			aria-selected={active}
			tabIndex={active ? 0 : -1}
			onClick={onClick}
			className={cn(
				"inline-flex min-h-[32px] shrink-0 items-center rounded-full border px-3 text-sm whitespace-nowrap transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 pointer-coarse:min-h-[44px]",
				active ? (scenario ? "border-[var(--scenario)] bg-[var(--scenario)] text-white" : "border-ink bg-ink text-white") : "bg-card hover:bg-accent",
				className,
			)}
		>
			{children}
		</button>
	);
}
